/**
 * ClientsList — Listado de clientes con tarjetas visuales, filtros por estado y edición/baja lógica.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Plus,
  Clock,
  ListTodo,
  Instagram,
  Loader2,
  Search,
  UserPlus,
  Settings,
  Archive,
  CheckCircle2,
  PauseCircle,
  LucideIcon,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { formatCurrency, formatDuration, getInitials, cn } from '@/lib/utils';
import type { Client } from '@/types';
import { ClientModal } from '@/components/clients/ClientModal';

const PLAN_LABELS: Record<string, string> = {
  basico: 'Básico',
  intermedio: 'Intermedio',
  premium: 'Premium',
};

const PLAN_VARIANTS: Record<string, 'secondary' | 'info' | 'default'> = {
  basico: 'secondary',
  intermedio: 'info',
  premium: 'default',
};

type StatusFilter = 'active' | 'paused' | 'inactive' | 'all';

interface StatusTab {
  id: StatusFilter;
  label: string;
  icon: LucideIcon;
  colorClass: string;
}

const STATUS_TABS: StatusTab[] = [
  { id: 'active', label: 'Activos', icon: CheckCircle2, colorClass: 'text-emerald-600' },
  { id: 'paused', label: 'Pausados', icon: PauseCircle, colorClass: 'text-amber-500' },
  { id: 'inactive', label: 'Inactivos', icon: Archive, colorClass: 'text-slate-500' },
  { id: 'all', label: 'Todos', icon: Users, colorClass: 'text-primary' },
];

export function ClientsList() {
  const [clients, setClients] = useState<Client[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [clientToEdit, setClientToEdit] = useState<Client | null>(null);

  const fetchClients = (status: StatusFilter = statusFilter) => {
    setLoading(true);
    api
      .getClients(status)
      .then((res) => setClients(res.clients as Client[]))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchClients(statusFilter);
  }, [statusFilter]);

  const handleClientCreated = (newClient: Client) => {
    if (statusFilter === 'all' || newClient.status === statusFilter) {
      setClients((prev) => [newClient, ...prev]);
    }
  };

  const handleClientUpdated = (updatedClient: Client) => {
    if (statusFilter !== 'all' && updatedClient.status !== statusFilter) {
      setClients((prev) => prev.filter((c) => c.id !== updatedClient.id));
    } else {
      setClients((prev) =>
        prev.map((c) => (c.id === updatedClient.id ? updatedClient : c))
      );
    }
    setClientToEdit(null);
  };

  const handleClientDeleted = (deletedId: string) => {
    if (statusFilter === 'active' || statusFilter === 'paused') {
      setClients((prev) => prev.filter((c) => c.id !== deletedId));
    } else if (statusFilter === 'all') {
      setClients((prev) =>
        prev.map((c) => (c.id === deletedId ? { ...c, status: 'inactive' } : c))
      );
    } else if (statusFilter === 'inactive') {
      fetchClients(statusFilter);
    }
    setClientToEdit(null);
  };

  const filteredClients = clients.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.business_name && c.business_name.toLowerCase().includes(search.toLowerCase())) ||
      (c.instagram && c.instagram.toLowerCase().includes(search.toLowerCase()))
  );

  const getSubtitle = () => {
    const count = filteredClients.length;
    switch (statusFilter) {
      case 'active':
        return `${count} ${count === 1 ? 'cliente activo' : 'clientes activos'} en gestión`;
      case 'paused':
        return `${count} ${count === 1 ? 'cliente pausado' : 'clientes pausados'}`;
      case 'inactive':
        return `${count} ${count === 1 ? 'cliente inactivo' : 'clientes inactivos'} (dados de baja)`;
      case 'all':
        return `${count} ${count === 1 ? 'cliente' : 'clientes'} en total`;
    }
  };

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-900">
            <Users size={28} className="text-primary" />
            Mis Clientes
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {getSubtitle()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {clients.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-muted-foreground w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar cliente o marca..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all w-48 sm:w-60"
              />
            </div>
          )}
          <Button onClick={() => setIsCreateModalOpen(true)} className="gap-2 shadow-sm">
            <Plus size={16} />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Filtro de Estados */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <div className="inline-flex items-center gap-1 p-1 bg-slate-100 rounded-xl border border-slate-200/80">
          {STATUS_TABS.map((tab) => {
            const Icon = tab.icon;
            const isSelected = statusFilter === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setStatusFilter(tab.id)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all',
                  isSelected
                    ? 'bg-white text-slate-900 shadow-xs border border-slate-200/60'
                    : 'text-slate-500 hover:text-slate-800 hover:bg-white/50'
                )}
              >
                <Icon size={14} className={isSelected ? tab.colorClass : 'text-slate-400'} />
                <span>{tab.label}</span>
                {isSelected && (
                  <span className="ml-0.5 px-1.5 py-0.2 bg-slate-100 text-slate-700 rounded-full text-[10px] font-bold">
                    {clients.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {statusFilter === 'inactive' && (
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200/80 px-3 py-1.5 rounded-lg flex items-center gap-2">
            <Archive size={13} className="text-slate-400 shrink-0" />
            <span>Los clientes inactivos conservan todo su historial de datos, tareas y archivos de Drive.</span>
          </div>
        )}
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredClients.length === 0 ? (
        /* Empty states */
        <div className="rounded-2xl border border-dashed border-slate-200 p-12 text-center bg-slate-50/50">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
            {statusFilter === 'inactive' ? (
              <Archive size={28} className="text-slate-500" />
            ) : statusFilter === 'paused' ? (
              <PauseCircle size={28} className="text-amber-500" />
            ) : (
              <UserPlus size={28} />
            )}
          </div>
          <h3 className="text-lg font-bold text-slate-800">
            {search
              ? 'No se encontraron clientes'
              : statusFilter === 'inactive'
              ? 'No hay clientes inactivos'
              : statusFilter === 'paused'
              ? 'No hay clientes pausados'
              : 'Aún no tienes clientes registrados'}
          </h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            {search
              ? `No hay clientes que coincidan con "${search}". Intenta con otro término.`
              : statusFilter === 'inactive'
              ? 'No has dado de baja a ningún cliente. Cuando des de baja a un cliente (soft delete), aparecerá aquí para que puedas consultarlo o reactivarlo en cualquier momento.'
              : statusFilter === 'paused'
              ? 'No tienes ningún cliente en pausa en este momento.'
              : 'Agrega tu primer cliente para comenzar a organizar su contenido, métricas, tareas y Google Drive.'}
          </p>
          {!search && (statusFilter === 'active' || statusFilter === 'all') && (
            <Button onClick={() => setIsCreateModalOpen(true)} className="mt-5 gap-2">
              <Plus size={16} />
              Crear mi primer cliente
            </Button>
          )}
        </div>
      ) : (
        /* Grid de Tarjetas */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <Link key={client.id} to={`/clientes/${client.id}`}>
              <Card className="group hover:shadow-md hover:border-primary/40 transition-all cursor-pointer h-full border border-slate-200/80">
                <CardContent className="p-5 flex flex-col justify-between h-full">
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3.5 min-w-0">
                        {/* Avatar */}
                        <div
                          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white font-bold text-sm shadow-xs transition-transform group-hover:scale-105"
                          style={{ backgroundColor: client.avatar_color }}
                        >
                          {getInitials(client.name)}
                        </div>

                        {/* Info */}
                        <div className="min-w-0">
                          <h3 className="font-semibold text-slate-900 truncate group-hover:text-primary transition-colors">
                            {client.name}
                          </h3>
                          {client.business_name && (
                            <p className="text-sm text-muted-foreground truncate">
                              {client.business_name}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Botón rápido para Editar / Baja Lógica */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setClientToEdit(client);
                        }}
                        className="p-1.5 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors shrink-0"
                        title="Editar o dar de baja cliente"
                      >
                        <Settings size={15} />
                      </button>
                    </div>

                    {/* Plan & Status Badges */}
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <Badge variant={PLAN_VARIANTS[client.plan_type]}>
                        {PLAN_LABELS[client.plan_type] || client.plan_type}
                      </Badge>
                      {client.status === 'inactive' && (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-300">
                          Inactivo
                        </span>
                      )}
                      {client.status === 'paused' && (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
                          Pausado
                        </span>
                      )}
                      {client.status === 'active' && statusFilter === 'all' && (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Activo
                        </span>
                      )}
                      <span className="text-sm font-semibold text-slate-700">
                        {formatCurrency(client.monthly_fee)}/mes
                      </span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 mt-4 pt-3 border-t text-xs text-muted-foreground">
                    <span className="flex items-center gap-1 font-medium">
                      <ListTodo size={14} className="text-slate-400" />
                      {client.pending_tasks ?? 0} tareas
                    </span>
                    <span className="flex items-center gap-1 font-medium">
                      <Clock size={14} className="text-slate-400" />
                      {formatDuration(client.monthly_hours_seconds ?? 0)}
                    </span>
                    {client.instagram && (
                      <span className="flex items-center gap-1 truncate text-pink-600 font-medium">
                        <Instagram size={14} />
                        {client.instagram}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Modal Nuevo Cliente */}
      <ClientModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleClientCreated}
      />

      {/* Modal Editar Cliente */}
      {clientToEdit && (
        <ClientModal
          isOpen={!!clientToEdit}
          onClose={() => setClientToEdit(null)}
          initialClient={clientToEdit}
          onSuccess={handleClientUpdated}
          onDelete={handleClientDeleted}
        />
      )}
    </div>
  );
}
